import {
  HOME_AI_BANNER_EXPERIMENT_ID,
  AI_BANNER_VARIANT_IDS,
} from "@sections/home/ai-banner-variants"

const AB_COOKIE_PREFIX = "kf_ab_"
const AB_SEEN_STORAGE_PREFIX = "kf_ab_seen_"
const CONSENT_COOKIE = "klipfolio_consent"
const HOMEPAGE_EXPERIMENT_ID = HOME_AI_BANNER_EXPERIMENT_ID

// Cloudflare serves challenger HTML for `/` unchanged, but Gatsby's client
// router reconciles the URL to the page component it actually hydrated
// (e.g. `/v4`) via history.replaceState once it loads. By the time this
// runs, real homepage traffic can show up as either path.
const HOMEPAGE_PATHS = new Set([
  "/",
  ...AI_BANNER_VARIANT_IDS.map(id => `/${id}`),
])

/**
 * @param {string} name
 */
function readCookie(name) {
  if (typeof document === "undefined") {
    return null
  }
  const parts = document.cookie.split(";")
  for (const part of parts) {
    const [key, ...rest] = part.trim().split("=")
    if (key === name) {
      return decodeURIComponent(rest.join("="))
    }
  }
  return null
}

function hasAnalyticsConsent() {
  const raw = readCookie(CONSENT_COOKIE)
  if (!raw) {
    return false
  }
  try {
    const parsed = JSON.parse(raw)
    return Boolean(parsed?.analyticsConsentGranted)
  } catch {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw))
      return Boolean(parsed?.analyticsConsentGranted)
    } catch {
      return false
    }
  }
}

/**
 * @param {string} experimentId
 */
function readAssignedVariant(experimentId) {
  return readCookie(`${AB_COOKIE_PREFIX}${experimentId}`)
}

/**
 * Set variant on dataLayer early (for enriched page_view) and fire ab_exposure once per session.
 */
export function trackAbExposure() {
  if (typeof window === "undefined") {
    return
  }
  if (!HOMEPAGE_PATHS.has(window.location.pathname)) {
    return
  }

  const experimentId = HOMEPAGE_EXPERIMENT_ID
  const variant = readAssignedVariant(experimentId)
  if (!variant) {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    experiment_id: experimentId,
    variant,
  })

  if (!hasAnalyticsConsent()) {
    return
  }

  const seenKey = `${AB_SEEN_STORAGE_PREFIX}${experimentId}`
  try {
    if (sessionStorage.getItem(seenKey)) {
      return
    }
    sessionStorage.setItem(seenKey, "1")
  } catch {
    // sessionStorage unavailable
  }

  window.dataLayer.push({
    event: "ab_exposure",
    experiment_id: experimentId,
    variant,
    page_path: window.location.pathname,
  })
}

/**
 * Fire a one-off A/B interaction event (banner click, form submit) to dataLayer.
 * Gated on analytics consent, same as ab_exposure.
 * @param {string} eventName
 * @param {Record<string, unknown>} payload
 */
export function trackAbEvent(eventName, payload) {
  if (typeof window === "undefined") {
    return
  }
  if (!hasAnalyticsConsent()) {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    ...payload,
  })
}

/**
 * Re-try exposure after consent is granted via the consent banner.
 */
export function registerAbExposureOnConsent() {
  if (typeof window === "undefined") {
    return
  }
  window.consentListeners = window.consentListeners || []
  window.consentListeners.push(consent => {
    if (consent?.analyticsConsentGranted) {
      trackAbExposure()
    }
  })
}
